import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import React, { useEffect } from "react";
import "react-datepicker/dist/react-datepicker.css";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import "semantic-ui-css/semantic.min.css";
import { MainContentLayout } from "./components";
import { IdleManager } from "./components/user-idel/IdleManager";
import { COMMON_PROTECT_ROUTE_PATH, IDLE_TIME, THEME, WARNING_TIME } from "./config/constants";
import { ConnectionError } from "./components/ui/ConnectionError";
import { PageLoader } from "./components/ui/PageLoader";
import { AuthContextProvider } from "./context/AuthContext";
import "./fonts.scss";
import { errorView } from "./helpers/ErrorHandler";
import { useAuthContext } from "./hooks/useAuthContext";
import { useThemeContext } from "./hooks/useThemeContext";
import { isAuthPath } from "./utils/authPath";
import "./index.css";
import "./index.scss";
import ProtectRoute from "./protectRoute";
import { MainRoutes } from "./routes";
import "./styles.scss";
import "./styles/tw-scope.css";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      cacheTime: 1,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      onError: (error: any) => {
        if (error?.status === 401) {
          window.location.href = "/auth";
          errorView(error?.msg);
        }
      }
    }
  }
});

const AppContent: React.FC = () => {
  const location = useLocation();
  const isAuthRoute = isAuthPath(location.pathname);
  const { status, retrySession } = useAuthContext();

  // Hold the UI until the session restore finishes so users with a valid session never see a
  // sign-in screen flash and nothing protected renders early.
  if (status === "loading") {
    return <PageLoader />;
  }

  if (status === "offline") {
    return <ConnectionError onRetry={retrySession} />;
  }

  return (
    <>
      {!isAuthRoute && <IdleManager idleTime={IDLE_TIME} warningTime={WARNING_TIME} />}
      <MainContentLayout>
        <Routes>
          {MainRoutes.map((routesDetails, index) => (
            <Route path={routesDetails.mainPath} key={index}>
              {routesDetails.routes.map((subRoutes, index) => (
                <Route
                  key={index}
                  path={subRoutes.path}
                  element={
                    <ProtectRoute
                      protectRoutes={subRoutes.protectRoutes}
                      roleGrantAccess={subRoutes?.roleGrantAccess}
                      redirectTo={COMMON_PROTECT_ROUTE_PATH}
                    >
                      {subRoutes.route}
                    </ProtectRoute>
                  }
                />
              ))}
            </Route>
          ))}
          <Route path="*" element={<h1>Not Found</h1>} />
        </Routes>
      </MainContentLayout>
    </>
  );
};

const App: React.FC = () => {
  const { dispatch } = useThemeContext();

  useEffect(() => {
    dispatch({ type: THEME });
  }, []);

  return (
    <div className="app">
      <AuthContextProvider>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <AppContent />
          </BrowserRouter>
        </QueryClientProvider>
      </AuthContextProvider>
    </div>
  );
};

export default App;
